import { Analytics } from '@analytics';
import TopNav from '@components/Nav/TopNav';
import { Check as CheckIcon, ChevronRight as ChevronIcon } from '@material-ui/icons';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import { orderCardLink } from '../../utils/constants';
import { useInjectReducer } from '../../utils/injectReducer';
import { useInjectSaga } from '../../utils/injectSaga';
import { TapToTipTabs } from '../TapToTipPage';
import reducer from '../TapToTipPage/reducer';
import saga from '../TapToTipPage/saga';

export const tippingLinkFor = (region, userId) => {
  switch (region) {
    case 'EU':
      return `https://eu.tip.direct/t/g/${userId}`;
    case 'QA':
      return `https://qats.tripadmit.com/t/g/${userId}`;
    default:
      return `https://us.tip.direct/t/g/${userId}`;
  }
};

const primaryClass =
  'w-full sm:flex-1 h-12 shrink-0 px-6 rounded-xl border-0 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white text-[15px] font-semibold cursor-pointer transition';
const secondaryClass =
  'w-full sm:flex-1 h-12 shrink-0 px-6 rounded-xl border border-solid border-slate-300 bg-white hover:border-slate-500 active:scale-[0.98] text-slate-900 text-[15px] font-semibold cursor-pointer transition';

const ExtraRow = ({ title, description, onClick, href }) => {
  const body = (
    <>
      <span className="min-w-0">
        <span className="block text-[15px] font-semibold text-slate-900">{title}</span>
        <span className="block text-sm text-slate-600 mt-0.5">{description}</span>
      </span>
      <ChevronIcon className="text-slate-400 shrink-0" />
    </>
  );
  const className =
    'flex items-center justify-between gap-4 w-full px-5 py-4 bg-white text-left no-underline border-0 cursor-pointer hover:bg-slate-50 transition-colors';
  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick}>
      {body}
    </a>
  ) : (
    <button type="button" className={className} onClick={onClick}>
      {body}
    </button>
  );
};

/**
 * End of first-login setup. One clear way on (the dashboard), the tipping link
 * to copy, and a preview of what guests see. Company owners also get the
 * invite-guides shortcut.
 */
const OnboardingCompletionPage = ({ user }) => {
  useInjectReducer({ key: 'taptotip', reducer });
  useInjectSaga({ key: 'taptotip', saga });
  const history = useHistory();
  const [copied, setCopied] = useState(false);

  const linkUrl = tippingLinkFor(process.env.REGION, user.userId);
  const isOwner = user.role === 'system_admin';

  const track = title => Analytics.track(`Onboarding Complete - ${title} Button Clicked`);

  const goToTab = (title, tab) => {
    track(title);
    history.push(`/tipdirect/${tab}`, { tab });
  };

  const copyLink = () => {
    Analytics.track('Onboarding Complete - Tipping Link Copied');
    if (navigator.clipboard) navigator.clipboard.writeText(linkUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <>
      <TopNav />
      <div className="w-full bg-slate-50 overflow-y-auto h-[calc(100%-77px)] md:h-[calc(100%-93px)]" data-testid="onboarding-complete">
        <div className="w-full max-w-[600px] mx-auto px-4 pt-8 md:pt-12 pb-12">
          <span className="flex items-center justify-center w-12 h-12 rounded-full bg-indigo-600 text-white">
            <CheckIcon />
          </span>
          <h1 className="text-2xl md:text-3xl font-black text-slate-900 m-0 mt-5">You're ready to take tips</h1>
          <p className="text-base text-slate-600 leading-relaxed mt-2 mb-0 max-w-[56ch]">
            Share your tipping link with guests. You can find it again on your dashboard.
          </p>

          <label htmlFor="onboarding-tipping-link" className="block text-sm font-semibold text-slate-700 mt-8 mb-2">
            Your tipping link
          </label>
          <div className="flex gap-2">
            <input
              id="onboarding-tipping-link"
              readOnly
              value={linkUrl}
              onFocus={e => e.target.select()}
              className="flex-1 min-w-0 box-border h-12 px-4 rounded-xl border border-solid border-slate-300 bg-white text-sm text-slate-900 focus:outline-none focus:border-slate-900"
            />
            <button
              type="button"
              onClick={copyLink}
              className="shrink-0 w-28 h-12 rounded-xl border border-solid border-slate-300 bg-white text-sm font-semibold text-slate-900 cursor-pointer hover:border-slate-500 transition-colors"
              aria-live="polite">
              {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <button
              type="button"
              className={primaryClass}
              onClick={() => goToTab('Go To Dashboard', TapToTipTabs.DASHBOARD.toLowerCase())}>
              Go to my dashboard
            </button>
            <button
              type="button"
              className={secondaryClass}
              onClick={() => {
                track('Preview Tipping Flow');
                window.open(`${linkUrl}?preview=true`, '_blank');
              }}>
              Preview tipping page
            </button>
          </div>

          <div className="mt-10 p-5 rounded-xl bg-white border border-solid border-slate-200">
            <p className="text-[15px] font-semibold text-slate-900 m-0">How do I get paid?</p>
            <p className="text-sm text-slate-600 leading-relaxed m-0 mt-1">
              Once you receive a tip we will send you an email with a link to claim it. Once you choose
              your payout method the rest of your tips will be sent automatically.
            </p>
          </div>

          <div className="mt-4 rounded-xl border border-solid border-slate-200 overflow-hidden divide-y divide-slate-200">
            {isOwner && (
              <ExtraRow
                title="Invite guides"
                description="Invite your guides to start using TipDirect."
                onClick={() => goToTab('Invite Guides', TapToTipTabs.USERS.toLowerCase())}
              />
            )}
            <ExtraRow
              title="Order cards"
              description="Need TipDirect cards? Order them through our website."
              href={orderCardLink}
              onClick={() => track('Order Cards')}
            />
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = state => ({
  user: state.user?.user
});

export default connect(mapStateToProps)(OnboardingCompletionPage);
